import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import ComicCard from "../../components/feature/ComicCard";
import { comics, getComic } from "../../mocks/manga";

const chaptersPreview = ["第1话 启程", "第2话 相遇", "第3话 试探", "第4话 危机", "第5话 抉择"];

export default function Detail() {
  const { id = "" } = useParams();
  const comic = getComic(id);
  const [descOpen, setDescOpen] = useState(false);

  const related = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.id !== comic.id && c.tags.some((t) => comic.tags.includes(t)))
      .slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="mx-auto max-w-7xl px-4 py-24 text-center md:px-6">
          <i className="ri-emotion-sad-line text-5xl text-foreground-400" aria-hidden="true"></i>
          <h1 className="mt-4 font-display text-2xl text-foreground-950">未找到该漫画</h1>
          <p className="mt-2 text-sm text-foreground-500">作品可能已下架或地址有误</p>
          <Link to="/library" className="mt-6 inline-block whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600">
            返回漫画大全
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://readdiary.github.io/" },
          { "@type": "ListItem", position: 2, name: "漫画大全", item: "https://readdiary.github.io/library" },
          { "@type": "ListItem", position: 3, name: comic.title, item: `https://readdiary.github.io/detail/${comic.id}` },
        ],
      },
      {
        "@type": "Book",
        name: comic.title,
        author: { "@type": "Person", name: comic.author },
        genre: comic.tags,
        description: comic.description,
        image: comic.cover,
        url: `https://readdiary.github.io/detail/${comic.id}`,
        inLanguage: "zh-CN",
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Navbar />

      <main>
        <section className="bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
            <nav aria-label="面包屑" className="mb-6 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <span className="mx-2">/</span>
              <Link to="/library" className="hover:text-primary-600">漫画大全</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground-700">{comic.title}</span>
            </nav>

            <div className="flex flex-col gap-8 lg:flex-row">
              <div className="shrink-0">
                <img
                  src={comic.cover}
                  alt={`${comic.title} 漫画封面`}
                  title={`${comic.title} 妖精漫画网`}
                  className="w-full max-w-[260px] rounded-2xl border border-background-200/70 object-cover object-top shadow-none lg:w-64"
                />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${
                    comic.status === "连载中" ? "bg-accent-500 text-foreground-950" : "bg-secondary-500 text-background-50"
                  }`}>
                    {comic.status}
                  </span>
                  {comic.tags.map((t) => (
                    <Link key={t} to={`/library?tag=${encodeURIComponent(t)}`} className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-600 transition-colors hover:bg-secondary-100 hover:text-secondary-900">
                      #{t}
                    </Link>
                  ))}
                </div>

                <h1 className="mt-4 font-display text-3xl leading-tight text-foreground-950 md:text-4xl">{comic.title}</h1>
                <p className="mt-2 text-sm text-foreground-600">作者：{comic.author} · {comic.year}年</p>

                <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <Stat icon="ri-star-fill" value={String(comic.rating)} label="评分" />
                  <Stat icon="ri-book-open-fill" value={`${comic.chapters}`} label="章节" />
                  <Stat icon="ri-eye-fill" value={comic.views} label="人气" />
                  <Stat icon="ri-refresh-line" value="每周更新" label="更新频率" />
                </div>

                <div className="mt-6 rounded-2xl border border-background-200/70 bg-background-50 p-5">
                  <h2 className="text-sm font-bold text-foreground-950">作品简介</h2>
                  <p className={`mt-2 text-sm leading-relaxed text-foreground-600 ${descOpen ? "" : "line-clamp-3"}`}>
                    {comic.description}
                  </p>
                  <button
                    type="button"
                    onClick={() => setDescOpen((v) => !v)}
                    className="mt-2 flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    {descOpen ? "收起" : "展开更多"} <i className={`${descOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-sm`} aria-hidden="true"></i>
                  </button>
                </div>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <button type="button" className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-8 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600">
                    <i className="ri-book-open-line text-base" aria-hidden="true"></i> 立即阅读
                  </button>
                  <button type="button" className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-8 py-3 text-sm font-bold text-foreground-950 transition-colors hover:bg-accent-600">
                    <i className="ri-bookmark-line text-base" aria-hidden="true"></i> 加入收藏
                  </button>
                  <button type="button" className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300/70 bg-background-50 px-8 py-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100">
                    <i className="ri-download-2-line text-base" aria-hidden="true"></i> 下载
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-10 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="mb-4 flex items-end justify-between">
                <h2 className="font-display text-2xl text-foreground-950">章节目录</h2>
                <span className="text-xs text-foreground-500">共 {comic.chapters} 话</span>
              </div>
              <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {[comic.latestChapter, ...chaptersPreview].map((ch, idx) => (
                  <li key={idx}>
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 rounded-xl border border-background-200/70 bg-background-50 px-4 py-3 text-left transition-colors hover:border-primary-300 hover:bg-primary-50"
                    >
                      <span className="text-xs font-bold text-primary-600">第{comic.chapters - idx}话</span>
                      <span className="flex-1 truncate text-sm text-foreground-700">{ch.split(" ").slice(1).join(" ") || ch}</span>
                      <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
                    </button>
                  </li>
                ))}
              </ul>
              <button type="button" className="mt-4 w-full whitespace-nowrap rounded-full border border-background-300/70 bg-background-100 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-200">
                查看全部 {comic.chapters} 话
              </button>
            </div>

            <aside>
              <h2 className="mb-4 font-display text-2xl text-foreground-950">同类推荐</h2>
              <div className="space-y-4">
                {related.map((c) => (
                  <Link key={c.id} to={`/detail/${c.id}`} className="group flex items-center gap-3 rounded-2xl border border-background-200/70 bg-background-50 p-3 transition-transform hover:-translate-y-0.5">
                    <img src={c.cover} alt={`${c.title} 封面`} className="h-20 w-14 shrink-0 rounded-lg object-cover object-top" />
                    <div className="min-w-0">
                      <p className="truncate font-medium text-foreground-950 group-hover:text-primary-600">{c.title}</p>
                      <p className="mt-1 flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-star-fill text-accent-500" aria-hidden="true"></i> {c.rating} · {c.chapters}话
                      </p>
                      <p className="mt-1 truncate text-xs text-foreground-400">{c.status} · {c.author}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </aside>
          </div>

          <div className="mt-12">
            <h2 className="mb-5 font-display text-2xl text-foreground-950">猜你喜欢</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {related.slice(0, 4).map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

function Stat({ icon, value, label }: { icon: string; value: string; label: string }) {
  return (
    <div className="rounded-2xl border border-background-200/70 bg-background-50 px-4 py-3">
      <p className="flex items-center gap-1.5 text-base font-bold text-foreground-950">
        <i className={`${icon} text-primary-500`} aria-hidden="true"></i> {value}
      </p>
      <p className="mt-0.5 text-xs text-foreground-500">{label}</p>
    </div>
  );
}